/**
 * MarkdownModeContentComponent — orchestration surface for the markdown-mode
 * plugin's `contentComponent` slot.
 *
 * This component is mounted by the editor's `contentComponent` mechanism and
 * is rendered as a sibling to the ProseMirror `editorDOMElement` inside
 * `FullPageContentArea`. It owns:
 *
 *  1. **Snapshot** — reads the current ADF doc from the live ProseMirror view
 *     when the user transitions away from WYSIWYG.
 *  2. **Dispatch tap** — installs a `dispatchTransaction` tap on the live
 *     ProseMirror view to push debounced `liveAdfDoc` updates to the source
 *     and preview views.
 *  3. **Source view rendering** — mounts `MarkdownSourceView` when
 *     `view === 'source'`, seeded from the snapshot and fed live updates.
 *  4. **Writeback** — pushes source-view edits back into the ProseMirror
 *     editor via `replaceWith` + `replaceDocument` meta (collab-safe).
 *  5. **Preview rendering** — delegates to a consumer-supplied
 *     `previewComponent` when `view === 'preview'`.
 *
 * The current view (`source | wysiwyg | preview`) lives in this plugin's
 * ProseMirror state (`markdownModePluginKey`); the wrapper that renders this
 * component reads it via `useSharedPluginStateSelector` and passes it down
 * as `currentView`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { MarkdownModePluginOptions, MarkdownModeView } from '../markdownModePluginType';
import { adfToMarkdown, markdownToAdf } from '../utils/markdown-conversion';

import { MarkdownSourceView } from './MarkdownSourceView';
const EMPTY_ADF_DOC: DocNode = { type: 'doc', version: 1, content: [] };

type MarkdownModeContentComponentProps = {
	config: MarkdownModePluginOptions;
	currentView: MarkdownModeView;
	editorView: EditorView;
};

export const MarkdownModeContentComponent = ({
	editorView,
	config,
	currentView,
}: MarkdownModeContentComponentProps): React.ReactElement | null => {
	if (!config.isMarkdownMode) {
		return null;
	}

	return (
		<MarkdownModeOrchestrator markdownView={currentView} editorView={editorView} config={config} />
	);
};

// -------------------------------------------------------------------
// Inner orchestrator — only mounts when isMarkdownMode is true.
// -------------------------------------------------------------------

type OrchestratorProps = {
	config: MarkdownModePluginOptions;
	editorView: EditorView;
	markdownView: 'source' | 'wysiwyg' | 'preview';
};

const MarkdownModeOrchestrator = ({ markdownView, editorView, config }: OrchestratorProps) => {
	// ---------------------------------------------------------------
	// State
	// ---------------------------------------------------------------
	const [adfDoc, setAdfDoc] = useState<DocNode>(EMPTY_ADF_DOC);
	const [snapshotRevision, setSnapshotRevision] = useState(0);
	const [liveAdfDoc, setLiveAdfDoc] = useState<DocNode | undefined>(undefined);
	const liveDocDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const dispatchTapInstalledRef = useRef(false);
	const pendingWritebackNodeRef = useRef<PMNode | null>(null);

	// ---------------------------------------------------------------
	// Snapshot — synchronously read the current doc from PM.
	// ---------------------------------------------------------------
	const snapshotAdf = useCallback((): DocNode | null => {
		if (!editorView || editorView.isDestroyed) {
			return null;
		}
		return editorView.state.doc.toJSON() as DocNode;
	}, [editorView]);

	// Snapshot whenever the user transitions away from WYSIWYG.
	useEffect(() => {
		if (markdownView === 'wysiwyg') {
			return;
		}
		const snap = snapshotAdf();
		if (snap && snap.type === 'doc') {
			setAdfDoc(snap);
			setSnapshotRevision((r) => r + 1);
			setLiveAdfDoc(undefined);
		}
	}, [markdownView, snapshotAdf]);

	// ---------------------------------------------------------------
	// Dispatch tap — push debounced live updates to source/preview.
	// ---------------------------------------------------------------
	useEffect(() => {
		if (markdownView === 'wysiwyg') {
			return;
		}
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (dispatchTapInstalledRef.current) {
			return;
		}

		const original = editorView.props.dispatchTransaction;

		const tap = (tr: Transaction) => {
			// Always run the original dispatcher first.
			if (original) {
				original.call(editorView, tr);
			} else {
				editorView.updateState(editorView.state.apply(tr));
			}

			if (!tr.docChanged) {
				return;
			}

			// Debounce: coalesce rapid keystrokes.
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
			}
			liveDocDebounceRef.current = setTimeout(() => {
				liveDocDebounceRef.current = null;
				if (!editorView || editorView.isDestroyed) {
					return;
				}
				const json = editorView.state.doc.toJSON() as DocNode;

				// Tier 2 echo guard via Node.eq
				if (pendingWritebackNodeRef.current !== null) {
					if (pendingWritebackNodeRef.current.eq(editorView.state.doc)) {
						pendingWritebackNodeRef.current = null;
						return;
					}
					pendingWritebackNodeRef.current = null;
				}

				setLiveAdfDoc(json);
			}, 150);
		};

		editorView.setProps({ dispatchTransaction: tap });
		dispatchTapInstalledRef.current = true;

		return () => {
			// Clean up debounce timer on unmount.
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
		};
	}, [markdownView, editorView]);

	// Clean up debounce on unmount.
	useEffect(() => {
		return () => {
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
		};
	}, []);

	// ---------------------------------------------------------------
	// Writeback handlers
	// ---------------------------------------------------------------

	// Stable ref for serializeFileMedia so it can be read inside
	// handleSourceMarkdownChange without adding it as a useCallback dep
	// (which would recreate the callback and re-register the source listener).
	const serializeFileMediaRef = useRef(config.serializeFileMedia);
	serializeFileMediaRef.current = config.serializeFileMedia;

	/**
	 * Called by MarkdownSourceView on every debounced source change (Tier 2).
	 * Parses markdown back to ADF and dispatches a replaceWith transaction.
	 */
	const handleSourceMarkdownChange = useCallback(
		(markdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			let adfNode: PMNode;
			try {
				adfNode = markdownToAdf(markdown, editorView.state.schema as Schema);
			} catch {
				return;
			}
			// Skip writeback if semantically equivalent to the current doc.
			//
			// We deliberately do NOT use `adfNode.eq(editorView.state.doc)` here
			// because ProseMirror's `Node.eq` is strict on attrs — nodes created
			// by `MarkdownTransformer` only carry the attrs the markdown syntax
			// can express (e.g. `url`, `alt`, `type` for media), while the live
			// editor doc carries extra default attrs assigned by the schema or
			// Confluence plugins (`width`, `height`, `__mediaTraceId`, etc.).
			// Those attrs differ even for semantically identical docs, so
			// `Node.eq` returns false and we'd fire a spurious `replaceDocument`
			// transaction that rebuilds media nodes with incomplete attrs —
			// causing them to be stripped or rendered as "Preview unavailable".
			//
			// Instead, round-trip the LIVE doc through the same markdown
			// pipeline and compare the resulting strings. If they're identical,
			// the two docs are semantically equivalent from the markdown
			// perspective and we can safely skip writeback (EDITOR-6636).
			try {
				const liveMarkdown = adfToMarkdown(
					editorView.state.doc,
					editorView.state.schema as Schema,
					{
						serializeFileMedia: serializeFileMediaRef.current,
					},
				);
				if (liveMarkdown === markdown) {
					return;
				}
			} catch {
				// Serialisation failed (e.g. unsupported node in live doc).
				// Fall through to writeback — it's better to attempt a repair
				// than to silently skip a real change.
			}
			try {
				const { state } = editorView;
				const tr = state.tr
					.replaceWith(0, state.doc.nodeSize - 2, adfNode.content)
					.setMeta('addToHistory', false)
					.setMeta('replaceDocument', true);
				pendingWritebackNodeRef.current = tr.doc;
				editorView.dispatch(tr);
			} catch {
				pendingWritebackNodeRef.current = null;
			}
		},
		[editorView],
	);

	/**
	 * Called by MarkdownSourceView on unmount / revision change when the
	 * user actually edited the buffer (Tier 0 — batch commit).
	 */
	const handleMarkdownCommit = useCallback(
		(finalMarkdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			try {
				const liveSchema = editorView.state.schema;
				const adfNode = markdownToAdf(finalMarkdown, liveSchema);
				const { state } = editorView;
				const tr = state.tr
					.replaceWith(0, state.doc.nodeSize - 2, adfNode.content)
					.setMeta('addToHistory', false)
					.setMeta('replaceDocument', true);
				editorView.dispatch(tr);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn(
					'[MarkdownModeContentComponent] Failed to apply markdown source edits back to the editor; discarding change.',
					err,
				);
			}
		},
		[editorView],
	);

	// ---------------------------------------------------------------
	// Hide the WYSIWYG ProseMirror DOM whenever the user is NOT in
	// WYSIWYG view (i.e. they're in source or preview).
	//
	// The platform's `FullPageContentArea` already attempts this via
	// `hideEditorContentAreaProsemirrorStyle`, but that style is gated on
	// `expValEqualsNoExposure('cc-markdown-mode', 'isEnabled', true)` AND
	// on the markdown-mode plugin's PM state being in sync — either of
	// which can fail (e.g. if the experiment value isn't loaded yet, or
	// if the contentComponent's view→PM sync hasn't run on first paint).
	// When that happens both the source CodeMirror AND the underlying
	// WYSIWYG ProseMirror render simultaneously (and any code-block
	// floating toolbar leaks through too).
	//
	// This effect targets the specific `editorView.dom` for this plugin
	// instance so it cannot affect other editors on the page, and
	// restores the original `display` value on cleanup so view→wysiwyg
	// transitions immediately re-show the editor.
	//
	// We also blur the editor when leaving WYSIWYG so floating toolbars
	// (code-block toolbar, table controls, etc.) that follow editor
	// selection don't remain visible over the source view.
	useEffect(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (markdownView === 'wysiwyg') {
			return;
		}

		const dom = editorView.dom;
		const originalDisplay = dom.style.display;
		dom.style.display = 'none';

		// Blur the editor to dismiss selection-anchored floating UI
		// (code-block toolbar, table controls). Guard against blur throwing
		// on a destroyed view.
		try {
			if (editorView.hasFocus()) {
				dom.blur();
			}
		} catch {
			// no-op
		}

		return () => {
			if (!editorView.isDestroyed) {
				dom.style.display = originalDisplay;
			}
		};
	}, [markdownView, editorView]);

	// ---------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------
	const isWysiwyg = markdownView === 'wysiwyg';

	if (isWysiwyg) {
		return null;
	}

	const PreviewComponent = config.previewComponent;

	return (
		<>
			{markdownView === 'source' && (
				<MarkdownSourceView
					contentId={config.contentId}
					initialAdfDoc={adfDoc}
					schema={editorView.state.schema}
					revision={snapshotRevision}
					liveAdfDoc={liveAdfDoc}
					onSourceMarkdownChange={handleSourceMarkdownChange}
					onMarkdownCommit={handleMarkdownCommit}
					serializeFileMedia={config.serializeFileMedia}
				/>
			)}
			{markdownView === 'preview' && PreviewComponent && (
				<PreviewComponent key={`preview-${snapshotRevision}`} adfDoc={liveAdfDoc ?? adfDoc} />
			)}
		</>
	);
};
